import React, { ChangeEvent, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { IconButton, Menu, MenuGroup, MenuTrigger, MenuContent, Checkbox, Text } from '@cad-arc';
import { useAdditionalInfos } from '../../hooks/use-additional-infos/additional-infos-provider';
import {
    useFilterConfig,
    FilterConfig,
} from '../../hooks/use-additional-infos/use-additional-infos-filter';
import { AdditionalInfoType } from '../../types';

export const AdditionalInfosFilter = observer(
    ({ isFirstResponder }: { isFirstResponder?: boolean }) => {
        const rawFilterConfig = useFilterConfig();

        const activeFilterConfig = useMemo(() => {
            return Object.entries(rawFilterConfig).reduce((acc, [key, config]) => {
                const isEnabled = config.checkEnabled ?? true;

                if (isEnabled) {
                    acc[key] = config;
                }

                return acc;
            }, {} as Record<string, FilterConfig>);
        }, [rawFilterConfig]);

        const defaultFilters = useMemo(() => {
            const filters = [AdditionalInfoType.USER];

            Object.values(activeFilterConfig).forEach((config) => {
                if (config.isDefaultEnabled) {
                    filters.push(...config.types);
                }
            });

            if (isFirstResponder && !activeFilterConfig.proqaUpdates) {
                filters.push(AdditionalInfoType.PROQA);
            }

            return filters;
        }, [activeFilterConfig, isFirstResponder]);

        const { filter = defaultFilters, setFilter } = useAdditionalInfos();

        const handleFilterChange = useCallback(
            (key: keyof typeof activeFilterConfig) => (e: ChangeEvent<HTMLInputElement>) => {
                const config = activeFilterConfig[key];

                if (!config) {
                    return;
                }

                const checked = e.target.checked;
                let newFilters: AdditionalInfoType[] = [];

                if (checked) {
                    newFilters = Array.from(new Set([...filter, ...config.types]));
                } else {
                    newFilters = filter.filter((f) => !config.types.includes(f));
                }

                setFilter(newFilters);
            },
            [filter, activeFilterConfig, setFilter]
        );

        return (
            <Menu>
                <MenuTrigger asChild>
                    <IconButton
                        aria-label="Filter"
                        icon="Filter"
                        variant="ghost"
                        data-testid="ADDITIONAL_INFOS_FILTER_BUTTON"
                    />
                </MenuTrigger>
                <MenuContent>
                    <MenuGroup
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 10,
                            padding: 5,
                        }}
                    >
                        <Text
                            color="tertiary"
                            fontWeight="semibold"
                            fontSize={isFirstResponder ? 'lg' : 'sm'}
                        >
                            Narrative Type
                        </Text>
                        {Object.entries(activeFilterConfig).map(([key, config]) => {
                            const isChecked = config.types.every((type) => filter.includes(type));

                            return (
                                <Checkbox
                                    key={key}
                                    onChange={handleFilterChange(
                                        key as keyof typeof activeFilterConfig
                                    )}
                                    isChecked={isChecked}
                                    data-testid={config.testId}
                                >
                                    {config.label}
                                </Checkbox>
                            );
                        })}
                    </MenuGroup>
                </MenuContent>
            </Menu>
        );
    }
);
